<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from '../axios.js'

const props = defineProps({
  playerId: {
    type: [String, Number],
    required: true
  },
  playerName: {
    type: String,
    required: true
  },
  templateLabel: {
    type: String,
    required: true
  },
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'saved'])

// State
const loading = ref(false)
const loadingTemplate = ref(false)
const saving = ref(false)
const template = ref(null)
const games = ref([])

// Data source selection - only 2 options
const dataSource = ref('clickhouse_boxscores') // clickhouse_boxscores or mysql_comparable

const dataSources = {
  clickhouse_boxscores: {
    label: 'ClickHouse (boxscores_data)',
    icon: 'fa-basketball-ball',
    color: 'orange',
    shortLabel: 'ClickHouse'
  },
  mysql_comparable: {
    label: 'MySQL (comparable_data)',
    icon: 'fa-database',
    color: 'blue',
    shortLabel: 'MySQL'
  }
}

// Settings - what will be displayed and edited
const settings = ref({
  seasons: [],
  leagues: [],
  additionalFilter: 'allGames'
})

// Available options
const availableSeasons = [
  '2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022',
  '2020-2021', '2019-2020', '2018-2019', '2017-2018'
]

const availableLeagues = [
  'nba',
  'wnba',
  'ncaa',
  'eurocup',
  'japan-b2-league',
  'australia-nbl',
  'argentina-liga-a',
  'bnxt-league',
  'japan-b1-league',
  'germany-pro-a',
  'croatia-liga',
  'france-elite-2',
  'puerto-rico-bsn',
  'preparation',
  'slovenia-liga-nova-kbm',
  'china-cba',
  'slovenia-liga-nova-kbm',
  'poland-plk',
  'new-zealand-nbl',
  'switzerland-sbl',
  'eurobasket',
  'bulgaria-nbl',
  'turkey-tbl',
  'turkey-bsl',
  'spain-leb-gold',
  'fiba-europe-cup',
  'euroleague',
  'greece-heba-a2',
  'olympic-games',
  'adriatic-league',
  'norway-blno',
  'italy-serie-a2',
  'world-cup',
  'greece-heba-a1',
  'serbia-kls',
  'denmark-ligaen',
  'hungary-a-league',
  'lithuania-nkl',
  'germany-easycredit-bbl',
  'super-league-basketball',
  'vtb-united-league',
  'finland-korisliiga',
  'iceland-express-league',
  'france-betclic-elite',
  'bosnia-division-i',
  'sweden-basketligan',
  'slovakia-extraliga',
  'lithuania-betsafe-lkl',
  'slovenia-liga-nova-kbm',
  'israel-winner-league',
  'cyprus-division-a',
  'adriatic-liga-aba',
  'italy-lba-serie-a',
  'mexico-liga-sisnova-lnbp',
  'adriatic-aba-liga-2',
  'austria-superliga',
  'georgia-superleague',
  'spain-liga-endesa',
  'romania-division-a',
  'eurobasket-qualifying-rounds',
  'latvian-estonian-basketball-league',
  'adriatic-liga-aba-playoffs',
  'basketball-champions-league-qualifying-rounds',
  'argentina-la-liga-torneo-super-20',
  'czech-republic-mattoni-nbl',
  'basketball-champions-league-americas',
  'lithuania-betsafe-lkl-playoffs',
  'olympic-games-qualifying-tournament',
  'france-betclic-elite-playoffs',
  'basketball-champions-league',
  'world-cup-european-qualifiers',
  'portugal-liga-profissional',
  'fiba-europe-cup-qualifying-rounds',
  'turkish-cup',
  'croatia-liga',
  'preparation',
  'fiba-europe-cup-qualifying-rounds',
  'brazilian-nbb',
  'brazil-nbb',
  'king-mindaugas-cup-play-offs',
  'latvian-cup',
  'enbl',
  'italian-cup',
  'spanish-cup',
  'world-cup-european-pre-qualifiers',
]

const additionalFilters = {
  allGames: 'All Games',
  lastFiveGames: 'Last 5 Games',
  withoutLast5Games: 'Rest of Season (Without Last 5)'
}

// Computed
const calculatedStats = computed(() => {
  // Always return stats object, even if no games (will be 0.00)
  const totalMinutes = games.value.reduce((sum, game) => sum + (parseFloat(game.minutes) || 0), 0)
  
  const stats = {
    points: games.value.reduce((sum, game) => sum + (parseFloat(game.points) || 0), 0),
    total_rebounds: games.value.reduce((sum, game) => sum + (parseFloat(game.total_rebounds) || 0), 0),
    assists: games.value.reduce((sum, game) => sum + (parseFloat(game.assists) || 0), 0),
    three_point_field_goal: games.value.reduce((sum, game) => sum + (parseFloat(game.three_point_field_goal) || 0), 0),
    steals: games.value.reduce((sum, game) => sum + (parseFloat(game.steals) || 0), 0),
    blocks: games.value.reduce((sum, game) => sum + (parseFloat(game.blocks) || 0), 0),
    turnovers: games.value.reduce((sum, game) => sum + (parseFloat(game.turnovers) || 0), 0)
  }
  
  return {
    gameCount: games.value.length,
    totalMinutes: totalMinutes.toFixed(1),
    per30: totalMinutes > 0 ? {
      points: ((stats.points / totalMinutes) * 30).toFixed(2),
      total_rebounds: ((stats.total_rebounds / totalMinutes) * 30).toFixed(2),
      assists: ((stats.assists / totalMinutes) * 30).toFixed(2),
      three_point_field_goal: ((stats.three_point_field_goal / totalMinutes) * 30).toFixed(2),
      steals: ((stats.steals / totalMinutes) * 30).toFixed(2),
      blocks: ((stats.blocks / totalMinutes) * 30).toFixed(2),
      turnovers: ((stats.turnovers / totalMinutes) * 30).toFixed(2)
    } : {
      // No games or no minutes - set all to 0.00
      points: '0.00',
      total_rebounds: '0.00',
      assists: '0.00',
      three_point_field_goal: '0.00',
      steals: '0.00',
      blocks: '0.00',
      turnovers: '0.00'
    }
  }
})

// Methods
async function loadTemplate() {
  loadingTemplate.value = true
  loading.value = true
  try {
    console.log('=== Loading template ===')
    console.log('Player ID:', props.playerId)
    console.log('Template Label:', props.templateLabel)
    
    // Fetch all templates for this player
    const response = await axios.get(`/api/v2/projections-players/splits/${props.playerId}`)
    const templates = response.data || []
    
    console.log('All templates:', templates)
    
    // Find the specific template
    const foundTemplate = templates.find(t => t.template_label === props.templateLabel)
    
    if (foundTemplate) {
      console.log('=== Template found ===')
      console.log('Raw template:', foundTemplate)
      
      template.value = foundTemplate
      
      // Parse seasons and leagues from JSON
      const parsedSeasons = parseJsonField(foundTemplate.seasons)
      const parsedLeagues = parseJsonField(foundTemplate.selected_leagues)
      
      console.log('Parsed seasons:', parsedSeasons)
      console.log('Parsed leagues:', parsedLeagues)
      console.log('Parsed leagues length:', parsedLeagues.length)
      console.log('Additional filter:', foundTemplate.additional_filter)
      
      settings.value.seasons = parsedSeasons
      settings.value.leagues = parsedLeagues
      settings.value.additionalFilter = foundTemplate.additional_filter || 'allGames'
      
      console.log('Settings updated:', settings.value)
      
      // Fetch games based on settings
      await fetchGames()
    } else {
      console.log('Template not found, creating new one')
      template.value = null
      // Set defaults
      settings.value = getDefaultSettings()
    }
  } catch (error) {
    console.error('Error loading template:', error)
  } finally {
    loadingTemplate.value = false
    loading.value = false
  }
}

function parseJsonField(field) {
  if (!field) return []
  if (Array.isArray(field)) return field
  if (typeof field === 'string') {
    try {
      return JSON.parse(field)
    } catch (e) {
      console.error('Error parsing JSON field:', e)
      return []
    }
  }
  return []
}

function getDefaultSettings() {
  // Default settings based on template type
  const defaults = {
    'Last Season': { seasons: ['2024-2025'], filter: 'allGames' },
    '2 Seasons ago': { seasons: ['2023-2024'], filter: 'allGames' },
    '3 Seasons ago': { seasons: ['2022-2023'], filter: 'allGames' },
    'Last 5 games': { seasons: ['2025-2026'], filter: 'lastFiveGames' },
    'Rest of Curr season': { seasons: ['2025-2026'], filter: 'withoutLast5Games' }
  }
  
  const config = defaults[props.templateLabel] || { seasons: ['2025-2026'], filter: 'allGames' }
  
  return {
    seasons: config.seasons,
    leagues: [], // Start with empty, don't pre-select all
    additionalFilter: config.filter
  }
}

async function fetchGames() {
  if (settings.value.seasons.length === 0 || settings.value.leagues.length === 0) {
    games.value = []
    return
  }
  
  loading.value = true
  try {
    console.log('=== Fetching games ===')
    console.log('Data source:', dataSource.value)
    console.log('Seasons:', settings.value.seasons)
    console.log('Leagues:', settings.value.leagues)
    console.log('Additional filter:', settings.value.additionalFilter)
    
    const response = await axios.post('/api/v2/games/player/filters', {
      player_id: props.playerId,
      seasons: settings.value.seasons,
      leagues: settings.value.leagues,
      additional_filter: settings.value.additionalFilter,
      data_source: dataSource.value // Pass data source to backend
    })
    
    let fetchedGames = response.data || []
    console.log('Fetched games count:', fetchedGames.length)
    console.log('Data source used:', dataSource.value)
    
    // Apply client-side additional filtering
    fetchedGames = applyAdditionalFilter(fetchedGames, settings.value.additionalFilter)
    console.log('After filter, games count:', fetchedGames.length)
    
    games.value = fetchedGames
  } catch (error) {
    console.error('Error fetching games:', error)
    games.value = []
  } finally {
    loading.value = false
  }
}

function applyAdditionalFilter(gamesData, filterType) {
  if (!gamesData || gamesData.length === 0) return gamesData
  
  switch (filterType) {
    case 'lastFiveGames':
      const sorted = [...gamesData].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sorted.slice(0, 5)
      
    case 'withoutLast5Games':
      const sortedForRest = [...gamesData].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedForRest.slice(5)
      
    case 'allGames':
    default:
      return gamesData
  }
}

async function saveTemplate() {
  // Validate at least one season is selected
  if (settings.value.seasons.length === 0) {
    alert('Please select at least one season')
    return
  }
  
  saving.value = true
  try {
    console.log('=== Saving template ===')
    console.log('Settings:', settings.value)
    console.log('Calculated stats:', calculatedStats.value)
    console.log('Games count:', games.value.length)
    
    const payload = {
      player_id: props.playerId,
      season_template: props.templateLabel,
      seasons: settings.value.seasons,
      leagues: settings.value.leagues.length > 0 ? settings.value.leagues : [], // Empty array = all leagues
      additional_filter: settings.value.additionalFilter,
      per30: {
        points: calculatedStats.value.per30.points,
        rebounds: calculatedStats.value.per30.total_rebounds, // Backend expects 'rebounds'
        assists: calculatedStats.value.per30.assists,
        three_pointers: calculatedStats.value.per30.three_point_field_goal, // Backend expects 'three_pointers'
        turnovers: calculatedStats.value.per30.turnovers,
        steals: calculatedStats.value.per30.steals,
        blocks: calculatedStats.value.per30.blocks
      }
    }
    
    console.log('Save payload:', payload)
    
    const response = await axios.post('/api/v2/projections-players/season-templates/save', payload)
    
    console.log('Save response:', response.data)
    
    if (games.value.length === 0) {
      alert('Template configuration saved! Stats are currently 0.00 and will be calculated when you run the recalculation command.')
    } else {
      alert(`Template saved successfully with ${games.value.length} games!`)
    }
    
    emit('saved')
  } catch (error) {
    console.error('Error saving template:', error)
    alert('Error saving template: ' + (error.response?.data?.message || error.message))
  } finally {
    saving.value = false
  }
}

// Toggle functions
function toggleSeason(season) {
  const index = settings.value.seasons.indexOf(season)
  if (index === -1) {
    settings.value.seasons.push(season)
  } else {
    settings.value.seasons.splice(index, 1)
  }
}

function toggleLeague(league) {
  const index = settings.value.leagues.indexOf(league)
  if (index === -1) {
    settings.value.leagues.push(league)
  } else {
    settings.value.leagues.splice(index, 1)
  }
}

function selectAllLeagues() {
  settings.value.leagues = [...availableLeagues]
}

function deselectAllLeagues() {
  settings.value.leagues = []
}

const allLeaguesSelected = computed(() => settings.value.leagues.length === availableLeagues.length)

// League search and filtering
const leagueSearchQuery = ref('')
const showSelectedOnly = ref(false)

const filteredAvailableLeagues = computed(() => {
  let leagues = availableLeagues
  
  // Filter by search query
  if (leagueSearchQuery.value.trim()) {
    const search = leagueSearchQuery.value.toLowerCase()
    leagues = leagues.filter(league => league.toLowerCase().includes(search))
  }
  
  // Filter by selected only
  if (showSelectedOnly.value) {
    leagues = leagues.filter(league => settings.value.leagues.includes(league))
  }
  
  return leagues
})

// Watchers - refetch games when settings change (but not during initial load)
watch(() => settings.value, () => {
  if (!loadingTemplate.value) {
    fetchGames()
  }
}, { deep: true })

// Watch data source changes - refetch games
watch(dataSource, () => {
  if (!loadingTemplate.value && settings.value.seasons.length > 0 && settings.value.leagues.length > 0) {
    console.log('Data source changed, refetching games...')
    fetchGames()
  }
})

// Initialize on mount
onMounted(() => {
  loadTemplate()
})

// Reload when props change
watch(() => [props.playerId, props.templateLabel], () => {
  if (props.show) {
    loadTemplate()
  }
})
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
    <div class="bg-white rounded-lg shadow-2xl w-full h-[95vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div class="flex items-center gap-4">
          <i class="fas fa-cog text-white text-2xl"></i>
          <div>
            <h2 class="text-xl font-bold text-white">{{ templateLabel }} Configuration</h2>
            <p class="text-blue-100 text-sm">
              Player ID: {{ playerId }} - {{ playerName }}
              <span v-if="template" class="ml-3">
                <i class="fas fa-check-circle"></i> Loaded from database
              </span>
            </p>
          </div>
        </div>
        
        <div class="flex items-center gap-4">
          <!-- Data Source Toggle -->
          <div class="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
            <label class="text-xs text-blue-100 font-medium block mb-2">
              <i class="fas fa-database mr-1"></i>
              Data Source:
            </label>
            <div class="flex gap-2">
              <button
                @click="dataSource = 'clickhouse_boxscores'"
                class="px-4 py-2 text-sm rounded transition-all duration-200 flex items-center gap-2"
                :class="dataSource === 'clickhouse_boxscores' 
                  ? 'bg-orange-500 text-white shadow-lg ring-2 ring-white/50 font-semibold' 
                  : 'bg-white/20 text-white hover:bg-white/30'"
              >
                <i class="fas fa-basketball-ball"></i>
                <span>ClickHouse</span>
              </button>
              <button
                @click="dataSource = 'mysql_comparable'"
                class="px-4 py-2 text-sm rounded transition-all duration-200 flex items-center gap-2"
                :class="dataSource === 'mysql_comparable' 
                  ? 'bg-blue-500 text-white shadow-lg ring-2 ring-white/50 font-semibold' 
                  : 'bg-white/20 text-white hover:bg-white/30'"
              >
                <i class="fas fa-database"></i>
                <span>MySQL</span>
              </button>
            </div>
            <div class="text-xs text-blue-100 mt-2">
              {{ dataSources[dataSource].label }}
            </div>
          </div>
          
          <button
            @click="emit('close')"
            class="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Left Sidebar - Filters -->
        <div class="w-80 bg-gray-50 border-r border-gray-200 flex flex-col overflow-y-auto p-6 space-y-6">
          
          <!-- Additional Filters -->
          <div>
            <h3 class="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
              <i class="fas fa-filter text-blue-500 mr-2"></i>
              Additional Filters
            </h3>
            <div v-if="loadingTemplate" class="flex items-center justify-center py-4">
              <i class="fas fa-spinner fa-spin text-blue-500"></i>
            </div>
            <div v-else class="space-y-2">
              <label
                v-for="(label, key) in additionalFilters"
                :key="key"
                class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                :class="settings.additionalFilter === key ? 'bg-blue-100 border border-blue-300' : 'bg-white border border-gray-200'"
              >
                <input
                  type="radio"
                  :value="key"
                  v-model="settings.additionalFilter"
                  class="form-radio h-4 w-4 text-blue-600"
                />
                <span class="text-sm font-medium" :class="settings.additionalFilter === key ? 'text-blue-700' : 'text-gray-700'">
                  {{ label }}
                </span>
              </label>
            </div>
          </div>

          <!-- Seasons -->
          <div>
            <h3 class="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
              <i class="fas fa-calendar text-green-500 mr-2"></i>
              Seasons ({{ loadingTemplate ? '...' : settings.seasons.length }})
            </h3>
            <div v-if="loadingTemplate" class="flex items-center justify-center py-4">
              <i class="fas fa-spinner fa-spin text-green-500"></i>
            </div>
            <div v-else class="space-y-2">
              <label
                v-for="season in availableSeasons"
                :key="season"
                class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-green-50 transition-colors"
                :class="settings.seasons.includes(season) ? 'bg-green-100 border border-green-300' : 'bg-white border border-gray-200'"
              >
                <input
                  type="checkbox"
                  :checked="settings.seasons.includes(season)"
                  @change="toggleSeason(season)"
                  class="form-checkbox h-4 w-4 text-green-600"
                />
                <span class="text-sm font-medium" :class="settings.seasons.includes(season) ? 'text-green-700' : 'text-gray-700'">
                  {{ season }}
                </span>
              </label>
            </div>
          </div>

          <!-- Leagues -->
          <div class="flex-1 flex flex-col min-h-0">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-sm font-bold text-gray-700 uppercase flex items-center">
                <i class="fas fa-trophy text-purple-500 mr-2"></i>
                Leagues ({{ loadingTemplate ? '...' : settings.leagues.length }})
              </h3>
              <button
                v-if="!loadingTemplate"
                @click="allLeaguesSelected ? deselectAllLeagues() : selectAllLeagues()"
                class="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
              >
                {{ allLeaguesSelected ? 'Clear All' : 'Select All' }}
              </button>
            </div>
            
            <!-- Loading State -->
            <div v-if="loadingTemplate" class="flex-1 flex items-center justify-center">
              <div class="text-center text-gray-500">
                <i class="fas fa-spinner fa-spin text-2xl mb-2 text-purple-500"></i>
                <p class="text-sm">Loading leagues from database...</p>
              </div>
            </div>
            
            <!-- Leagues Content -->
            <div v-else class="flex-1 flex flex-col min-h-0">
              <!-- Search Input -->
              <div class="mb-2">
                <div class="relative">
                  <input
                    v-model="leagueSearchQuery"
                    type="text"
                    placeholder="Search leagues..."
                    class="w-full px-3 py-2 pr-8 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <i class="fas fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"></i>
                </div>
                
                <!-- Filter Options -->
                <div class="flex items-center justify-between mt-2">
                  <button
                    @click="showSelectedOnly = !showSelectedOnly"
                    class="text-xs px-2 py-1 rounded transition-colors"
                    :class="showSelectedOnly ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
                  >
                    <i class="fas" :class="showSelectedOnly ? 'fa-eye' : 'fa-eye-slash'"></i>
                    {{ showSelectedOnly ? 'Show All' : 'Selected Only' }}
                  </button>
                  
                  <div class="text-xs text-gray-600">
                    {{ filteredAvailableLeagues.length }} 
                    {{ showSelectedOnly ? 'selected' : 'leagues' }}
                  </div>
                </div>
                
                <div v-if="leagueSearchQuery && !showSelectedOnly" class="text-xs text-gray-600 mt-1">
                  <i class="fas fa-filter mr-1"></i>
                  Filtered to {{ filteredAvailableLeagues.length }} leagues
                </div>
                <div v-if="leagueSearchQuery && filteredAvailableLeagues.length === 0" class="text-xs text-red-600 mt-1">
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  No matches
                </div>
              </div>
              
              <!-- No Leagues Warning -->
              <div v-if="settings.leagues.length === 0" class="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2">
                <p class="text-xs text-yellow-700">
                  <i class="fas fa-info-circle mr-1"></i>
                  No leagues selected (not recommended)
                </p>
              </div>
              
              <!-- Leagues List -->
              <div class="flex-1 overflow-y-auto space-y-1">
                <label
                  v-for="league in filteredAvailableLeagues"
                  :key="league"
                  class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                  :class="settings.leagues.includes(league) ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-gray-200'"
                >
                  <input
                    type="checkbox"
                    :checked="settings.leagues.includes(league)"
                    @change="toggleLeague(league)"
                    class="form-checkbox h-4 w-4 text-purple-600"
                  />
                  <span class="text-xs" :class="settings.leagues.includes(league) ? 'text-purple-700 font-medium' : 'text-gray-700'">
                    {{ league }}
                  </span>
                </label>
              </div>
            </div>
          </div>

        </div>

        <!-- Right Content - Stats & Games -->
        <div class="flex-1 flex flex-col overflow-hidden bg-white">
          
          <!-- Stats Summary -->
          <div v-if="calculatedStats" class="bg-gradient-to-r from-gray-50 to-blue-50 p-6 border-b border-gray-200">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-bold text-gray-800">
                <i class="fas fa-chart-bar mr-2 text-blue-500"></i>
                Calculated Per30 Stats
              </h3>
              <div v-if="games.length === 0" class="bg-yellow-100 border border-yellow-300 rounded px-3 py-1 text-xs text-yellow-800 font-medium">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                Template config saved - stats will be recalculated automatically
              </div>
            </div>
            <div class="grid grid-cols-8 gap-4">
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">PTS</div>
                <div class="text-2xl font-bold text-blue-600">{{ calculatedStats.per30.points }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">REB</div>
                <div class="text-2xl font-bold text-green-600">{{ calculatedStats.per30.total_rebounds }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">AST</div>
                <div class="text-2xl font-bold text-purple-600">{{ calculatedStats.per30.assists }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">3PM</div>
                <div class="text-2xl font-bold text-orange-600">{{ calculatedStats.per30.three_point_field_goal }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">STL</div>
                <div class="text-2xl font-bold text-teal-600">{{ calculatedStats.per30.steals }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">BLK</div>
                <div class="text-2xl font-bold text-red-600">{{ calculatedStats.per30.blocks }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">TO</div>
                <div class="text-2xl font-bold text-gray-600">{{ calculatedStats.per30.turnovers }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200 text-center">
                <div class="text-xs text-gray-500 font-medium">Games</div>
                <div class="text-2xl font-bold text-indigo-600">{{ calculatedStats.gameCount }}</div>
              </div>
            </div>
            <div class="mt-3 text-sm text-gray-600 text-center">
              Total Minutes: {{ calculatedStats.totalMinutes }}
            </div>
          </div>

          <!-- Games Table -->
          <div class="flex-1 overflow-auto p-6">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-3">
                <h3 class="text-lg font-bold text-gray-800">
                  <i class="fas fa-basketball-ball mr-2 text-orange-500"></i>
                  Games List ({{ games.length }})
                </h3>
                <span 
                  class="text-xs px-2 py-1 rounded font-medium"
                  :class="{
                    'bg-orange-100 text-orange-700': dataSource === 'clickhouse_boxscores',
                    'bg-blue-100 text-blue-700': dataSource === 'mysql_comparable'
                  }"
                >
                  <i class="fas mr-1" :class="dataSources[dataSource].icon"></i>
                  {{ dataSources[dataSource].label }}
                </span>
              </div>
              <button
                @click="saveTemplate"
                :disabled="saving || loadingTemplate || settings.seasons.length === 0"
                class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
              >
                <i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                {{ saving ? 'Saving...' : loadingTemplate ? 'Loading...' : 'Save Template' }}
              </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex items-center justify-center py-12">
              <div class="text-center text-gray-500">
                <i class="fas fa-spinner fa-spin text-4xl mb-4"></i>
                <p>Loading games...</p>
              </div>
            </div>

            <!-- No Games -->
            <div v-else-if="games.length === 0" class="flex items-center justify-center py-12">
              <div class="text-center">
                <i class="fas fa-basketball-ball text-4xl mb-4 text-gray-300"></i>
                <p class="text-gray-500 mb-2">No games found with selected filters</p>
                <p class="text-sm text-gray-400 mb-4">Try selecting different seasons or leagues</p>
                
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                  <div class="flex items-start gap-3">
                    <i class="fas fa-info-circle text-blue-500 mt-1"></i>
                    <div class="text-left text-sm text-blue-800">
                      <p class="font-semibold mb-2">You can still save this template!</p>
                      <p class="text-blue-700">
                        Stats will show as 0.00 for now, but will be automatically recalculated when you run:
                      </p>
                      <code class="block bg-blue-100 text-blue-900 px-3 py-2 rounded mt-2 text-xs font-mono">
                        php artisan projection:update-per30-templates --recalculate --bulk-mode
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Games Table -->
            <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th class="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-700">Season</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-700">League</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-700">Team</th>
                    <th class="px-3 py-2 text-left font-semibold text-gray-700">Opponent</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">Min</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">PTS</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">REB</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">AST</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">3PM</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">STL</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">BLK</th>
                    <th class="px-3 py-2 text-center font-semibold text-gray-700">TO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(game, index) in games"
                    :key="index"
                    class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td class="px-3 py-2 text-gray-700">{{ game.game_date }}</td>
                    <td class="px-3 py-2 text-gray-700">{{ game.season }}</td>
                    <td class="px-3 py-2 text-gray-600 text-xs">{{ game.league_name }}</td>
                    <td class="px-3 py-2 text-gray-700">{{ game.team_name }}</td>
                    <td class="px-3 py-2 text-gray-700">{{ game.opponent_team_name }}</td>
                    <td class="px-3 py-2 text-center font-medium">{{ game.minutes }}</td>
                    <td class="px-3 py-2 text-center font-medium text-blue-600">{{ game.points }}</td>
                    <td class="px-3 py-2 text-center font-medium text-green-600">{{ game.total_rebounds }}</td>
                    <td class="px-3 py-2 text-center font-medium text-purple-600">{{ game.assists }}</td>
                    <td class="px-3 py-2 text-center font-medium text-orange-600">{{ game.three_point_field_goal }}</td>
                    <td class="px-3 py-2 text-center font-medium text-teal-600">{{ game.steals }}</td>
                    <td class="px-3 py-2 text-center font-medium text-red-600">{{ game.blocks }}</td>
                    <td class="px-3 py-2 text-center font-medium text-gray-600">{{ game.turnovers }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      <!-- Footer -->
      <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
        <div class="text-sm text-gray-600">
          <div class="flex items-center gap-2">
            <i class="fas fa-info-circle"></i>
            <div>
              <div>Changes will be saved to <code class="bg-gray-200 px-2 py-1 rounded text-xs">projection_per30_templates</code> table</div>
              <div class="text-xs text-gray-500 mt-1">
                Currently viewing: 
                <code 
                  class="px-2 py-0.5 rounded text-xs font-medium"
                  :class="{
                    'bg-orange-100 text-orange-700': dataSource === 'clickhouse_boxscores',
                    'bg-blue-100 text-blue-700': dataSource === 'mysql_comparable'
                  }"
                >
                  {{ dataSources[dataSource].label }}
                </code>
              </div>
            </div>
          </div>
        </div>
        <div class="flex gap-3">
          <button
            @click="emit('close')"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            @click="saveTemplate"
            :disabled="saving || loadingTemplate || settings.seasons.length === 0"
            class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
          >
            <i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
            {{ saving ? 'Saving...' : loadingTemplate ? 'Loading...' : 'Save Template' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

